/**
 * BeneficialOwnerWorksheet — FinCEN CDD Beneficial Ownership editor (Screen 38).
 *
 * BACKLOG-0721b (Bill 2026-06-01): mounted on the BUSINESS-ENTITY person dialog
 * (the 'ownership' tab), NOT the product modal. Captures the FinCEN CDD Rule
 * (31 CFR 1010.230) ownership prong (>=25% owners) + control prong (1 controller)
 * for legal-entity customers, per the canonical bo-worksheet (SPEC.md:3053-3063,
 * index.html:3338-3414). Data persists on person.extra.beneficialOwners; the
 * compliance gates read a metadataJson.beneficialOwners mirror written by
 * PeopleStep.persist (toBoMirror).
 *
 * Replaces the read-only Wave-1 scaffold ownership/BeneficialOwnerSection.tsx.
 *
 * Core Director destinations (no hallucinated fields — CLAUDE.md rule #2):
 *   ownership prong -> CIF 363-366 · control prong -> CIF 367 · exempt -> CIF 378.
 */
import { Banner, DateTextInput, Pill } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import {
  type BeneficialOwnerEntry,
  boRequirementFor,
  readBeneficialOwners,
  readBoExempt,
  benflOwnType,
  boAcctRelCode,
  BO_TITLE_OPTIONS,
  BO_ID_TYPE_OPTIONS,
} from './beneficialOwners';

/** FinCEN: at most 4 individuals can each own >=25%. */
const MAX_OWNERS = 4;

const makeId = (): string => `bo_${Math.random().toString(36).slice(2, 10)}`;

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--ink-600)',
  marginBottom: 3,
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 13,
  border: '1px solid var(--ink-300)',
  borderRadius: 'var(--r-sm)',
  background: 'var(--surface-card, #fff)',
  color: 'var(--ink-900, #1d1d1f)',
  boxSizing: 'border-box',
};
const fieldWrap: React.CSSProperties = { flex: '1 1 120px', minWidth: 0 };

export interface BeneficialOwnerWorksheetProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  isChanged?: (field: string) => boolean;
  readOnly?: boolean;
}

export function BeneficialOwnerWorksheet({
  person,
  onChange,
  readOnly = false,
}: BeneficialOwnerWorksheetProps): JSX.Element | null {
  const req = boRequirementFor(person.customerType);
  if (req.kind === 'none') return null;

  const isFull = req.kind === 'full';
  const entries = readBeneficialOwners(person.extra);
  const { exempt, reason } = readBoExempt(person.extra);

  const writeEntries = (next: BeneficialOwnerEntry[]): void => {
    onChange({ extra: { ...(person.extra ?? {}), beneficialOwners: next } });
  };
  const patchExtra = (patch: Record<string, unknown>): void => {
    onChange({ extra: { ...(person.extra ?? {}), ...patch } });
  };

  const addOwner = (controller: boolean): void => {
    writeEntries([
      ...entries,
      {
        id: makeId(),
        name: '',
        isController: controller,
        ...(controller ? {} : { ownershipPct: 25 }),
      },
    ]);
  };
  const updateOwner = (id: string, patch: Partial<BeneficialOwnerEntry>): void => {
    writeEntries(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };
  const removeOwner = (id: string): void => {
    writeEntries(entries.filter((e) => e.id !== id));
  };

  // ── Derived validation ──────────────────────────────────────────────
  const controllerCount = entries.filter((e) => e.isController).length;
  const ownerCount = entries.filter((e) => (e.ownershipPct ?? 0) >= 25).length;
  const ownerPctTotal = entries
    .filter((e) => !e.isController)
    .reduce((sum, e) => sum + (e.ownershipPct ?? 0), 0);
  const ownerProngFull = ownerCount >= MAX_OWNERS;
  const namesMissing = entries.some((e) => !e.name.trim());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14, color: 'var(--ink-800)' }}>
            Beneficial Ownership (FinCEN CDD)
          </strong>
          <Pill variant="info">{isFull ? '25%+ owners + Control person' : 'Control person only'}</Pill>
        </div>
        <p style={{ fontSize: 11, color: 'var(--ink-600)', margin: '4px 0 0', lineHeight: 1.5 }}>
          31 CFR 1010.230 — capture each individual who owns ≥25% equity (ownership prong, CIF 363-366)
          and one individual with substantial control (control prong, CIF 367). <span style={{ fontStyle: 'italic' }}>{req.citation}</span>
        </p>
      </div>

      {/* BO-exempt toggle (FULL only) — "no individual owns 25%+". CIF Field 378. */}
      {isFull && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 10px', background: 'var(--ink-50)', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}>
            <input
              type="checkbox"
              checked={exempt}
              disabled={readOnly}
              onChange={(e) => patchExtra({ boExempt: e.currentTarget.checked })}
            />
            No individual owns 25% or more (ownership-prong exempt)
          </label>
          {exempt && (
            <div>
              <label style={labelStyle} htmlFor="rao-bo-exempt-reason">Basis (no individual owns ≥25%)</label>
              <input
                id="rao-bo-exempt-reason"
                style={inputStyle}
                value={reason}
                disabled={readOnly}
                placeholder="e.g. widely-held / no 25% owner / regulated parent"
                onChange={(e) => patchExtra({ boExemptReason: e.currentTarget.value })}
              />
              <p style={{ fontSize: 10, color: 'var(--ink-500)', margin: '4px 0 0', fontStyle: 'italic' }}>
                A control person is still required. (Distinct from a full CDD exemption — CIF Field 378
                Exempt-Person-Class is a coded field captured for CDD-exempt entity types only.)
              </p>
            </div>
          )}
        </div>
      )}

      {/* Validation banners */}
      {controllerCount === 0 && (
        <Banner tone="warn">
          A <strong>control person</strong> is required for CDD compliance (control prong, CIF 367).
          Add at least one entry flagged as the controller.
        </Banner>
      )}
      {isFull && !exempt && ownerCount === 0 && (
        <Banner tone="info">
          No ≥25% owner captured yet. Add each individual owning 25%+ — or check the exemption above
          if none exists.
        </Banner>
      )}
      {isFull && !exempt && ownerPctTotal > 100 && (
        <Banner tone="err">Ownership total {ownerPctTotal}% exceeds 100% — check the equity percentages.</Banner>
      )}
      {namesMissing && (
        <Banner tone="warn">Every beneficial owner / controller needs a full legal name.</Banner>
      )}

      {/* Owner / controller cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-sm)', color: 'var(--ink-600)', fontSize: 12 }}>
            No beneficial owners or control persons added yet.
          </div>
        )}
        {entries.map((e) => {
          const rel = boAcctRelCode(e);
          const type = benflOwnType(e);
          return (
            <div key={e.id} style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--surface-card, #fff)', padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {e.isController && <Pill variant="info">Controller</Pill>}
                <Pill variant="muted">{type} → AcctRelCode {rel}</Pill>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removeOwner(e.id)}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--pcb-red, #b10505)', cursor: 'pointer', fontSize: 12 }}
                  >
                    Remove ×
                  </button>
                )}
              </div>

              {/* Row 1 — name / title / own% / controller */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ ...fieldWrap, flex: '2 1 180px' }}>
                  <label style={labelStyle}>Full legal name</label>
                  <input style={inputStyle} value={e.name} disabled={readOnly}
                    onChange={(ev) => updateOwner(e.id, { name: ev.currentTarget.value })} />
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Title</label>
                  <select style={inputStyle} value={e.title ?? ''} disabled={readOnly}
                    onChange={(ev) => updateOwner(e.id, { title: ev.currentTarget.value || undefined })}>
                    <option value="">—</option>
                    {BO_TITLE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                {isFull && (
                  <div style={{ ...fieldWrap, flex: '0 1 90px' }}>
                    <label style={labelStyle}>Ownership %</label>
                    <input type="number" min={0} max={100} step={0.01} style={inputStyle}
                      value={e.ownershipPct ?? ''} disabled={readOnly}
                      onChange={(ev) => updateOwner(e.id, { ownershipPct: ev.currentTarget.value === '' ? undefined : Number(ev.currentTarget.value) })} />
                  </div>
                )}
                <div style={{ ...fieldWrap, flex: '0 1 120px', display: 'flex', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-700)' }}>
                    <input type="checkbox" checked={e.isController} disabled={readOnly}
                      onChange={(ev) => updateOwner(e.id, { isController: ev.currentTarget.checked })} />
                    Control person
                  </label>
                </div>
              </div>

              {/* Row 2 — SSN / DOB / phone */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={fieldWrap}>
                  <label style={labelStyle}>SSN</label>
                  <input style={inputStyle} value={e.ssn ?? ''} disabled={readOnly} inputMode="numeric"
                    onChange={(ev) => updateOwner(e.id, { ssn: ev.currentTarget.value.replace(/\D/g, '').slice(0, 9) })} />
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Date of birth</label>
                  {/* Typed MM/DD/YYYY (Collette Cruse 2026-10-16: number-keypad entry). */}
                  <DateTextInput style={inputStyle} value={e.dob ?? ''} disabled={readOnly}
                    aria-label="Date of birth"
                    onChange={(iso) => updateOwner(e.id, { dob: iso })} />
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Phone</label>
                  <input style={inputStyle} value={e.phone ?? ''} disabled={readOnly}
                    onChange={(ev) => updateOwner(e.id, { phone: ev.currentTarget.value })} />
                </div>
              </div>

              {/* Row 3 — address */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ ...fieldWrap, flex: '2 1 200px' }}>
                  <label style={labelStyle}>Street address</label>
                  <input style={inputStyle} value={e.addr ?? ''} disabled={readOnly}
                    onChange={(ev) => updateOwner(e.id, { addr: ev.currentTarget.value })} />
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>City</label>
                  <input style={inputStyle} value={e.city ?? ''} disabled={readOnly}
                    onChange={(ev) => updateOwner(e.id, { city: ev.currentTarget.value })} />
                </div>
                <div style={{ ...fieldWrap, flex: '0 1 70px' }}>
                  <label style={labelStyle}>State</label>
                  <input style={inputStyle} value={e.state ?? ''} disabled={readOnly} maxLength={2}
                    onChange={(ev) => updateOwner(e.id, { state: ev.currentTarget.value.toUpperCase().slice(0, 2) })} />
                </div>
                <div style={{ ...fieldWrap, flex: '0 1 90px' }}>
                  <label style={labelStyle}>ZIP</label>
                  <input style={inputStyle} value={e.zip ?? ''} disabled={readOnly}
                    onChange={(ev) => updateOwner(e.id, { zip: ev.currentTarget.value.replace(/[^\d-]/g, '').slice(0, 10) })} />
                </div>
              </div>

              {/* Row 4 — government ID */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <div style={fieldWrap}>
                  <label style={labelStyle}>ID type</label>
                  <select style={inputStyle} value={e.idType ?? ''} disabled={readOnly}
                    onChange={(ev) => updateOwner(e.id, { idType: ev.currentTarget.value || undefined })}>
                    <option value="">—</option>
                    {BO_ID_TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>ID number</label>
                  <input style={inputStyle} value={e.idNumber ?? ''} disabled={readOnly}
                    onChange={(ev) => updateOwner(e.id, { idNumber: ev.currentTarget.value })} />
                </div>
                <div style={fieldWrap}>
                  <label style={labelStyle}>Issuer (state/country)</label>
                  <input style={inputStyle} value={e.idIssuer ?? ''} disabled={readOnly}
                    onChange={(ev) => updateOwner(e.id, { idIssuer: ev.currentTarget.value })} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add buttons */}
      {!readOnly && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isFull && !exempt && (
            <button type="button" onClick={() => addOwner(false)} disabled={ownerProngFull}
              title={ownerProngFull ? 'At most 4 individuals can each own ≥25%' : undefined}
              style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue)', background: 'var(--info-bg)', border: '1px solid var(--pcb-blue)', borderRadius: 'var(--r-sm)', cursor: ownerProngFull ? 'not-allowed' : 'pointer', opacity: ownerProngFull ? 0.5 : 1 }}>
              + Add owner (≥25%)
            </button>
          )}
          <button type="button" onClick={() => addOwner(true)}
            style={{ padding: '6px 12px', fontSize: 12, fontWeight: 600, color: 'var(--pcb-blue-dark)', background: 'var(--info-bg)', border: '1px solid var(--pcb-blue-dark)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}>
            + Add control person
          </button>
        </div>
      )}

      {controllerCount > 0 && (!isFull || exempt || ownerCount > 0) && !namesMissing && (
        <Banner tone="ok">Beneficial ownership complete — {controllerCount} control person{controllerCount === 1 ? '' : 's'}{isFull && !exempt ? `, ${ownerCount} owner${ownerCount === 1 ? '' : 's'} ≥25%` : ''}.</Banner>
      )}
    </div>
  );
}
